import { parse, validate } from "fast-xml-parser";
import { first, forEach, groupBy, isBoolean, isNumber, map, padStart } from "lodash";
import { DangerousGoodsInput, HandlingInput, HandlingType } from "../types/graph";
import { DeepPartialNotListed, DeepPartialNotListedAndOverride, PackingGroup } from "../types";
import { PackagedDangerousGoods, PmisData, PmisUMPackagedDangerousGoods } from "../types/pmis-data";

const handlingTypesByPmisDataType: Record<keyof PmisData, HandlingType> = {
    packagedDangerousGoodsTransit: HandlingType.Transit,
    packagedDangerousGoodsBoarding: HandlingType.Boarding,
    packagedDangerousGoodsUnboarding: HandlingType.Unboarding,
    packagedDangerousGoodsBoardingFromTranship: HandlingType.TranshipmentBoarding,
    packagedDangerousGoodsUnboardingToTranship: HandlingType.TranshipmentUnboarding
}

const subsidiaryRiskRegex = /[0-9](\.[0-9])?/g

const pgFromXml = (xmlPg: string | null) => {
    switch (xmlPg ?? '') {
        case 'I':
        case 'II':
        case 'III':
            return xmlPg as PackingGroup;
    }
    return '';
}

const isoCodeSizeRegex = /[1234BCGHLMN][02345689CDEF]/i;
const isoCodeTypeRegex = /[A-Z]([A-Z0-9]|$)/i;

const loadintUnitSizeAndType = (isoCode: string) => {
    const sizePos = isoCode.search(isoCodeSizeRegex);

    if (sizePos < 0) {
        return [null, null];
    }

    let size: string | null;

    switch (isoCode[sizePos]) {
        case '1':
            size = '10';
            break;
        case '2':
            size = '20';
            break;
        case '3':
            size = '30';
            break;
        case '4':
            size = '40';
            break;
        case 'B':
            size = '24';
            break;
        case 'C':
            size = '24.5';
            break;
        case 'G':
            size = '41';
            break;
        case 'H':
            size = '43';
            break;
        case 'L':
            size = '45';
            break;
        case 'M':
            size = '48';
            break;
        case 'N':
            size = '49';
            break;
        default:
            size = null;
    }

    const typeSearchStart = sizePos + 2;

    const typePos = typeSearchStart + isoCode.substr(typeSearchStart).search(isoCodeTypeRegex);

    if (typePos < typeSearchStart) {
        return [size, null];
    }

    const typeCode = isoCode.substr(typePos, Math.min(2, isoCode.length - typePos));

    switch (typeCode[0].toUpperCase()) {
        case 'T':
            return [size, 'TANK']
        case 'B':
            return [size, 'BULK']
    }

    return [size, typeCode];
}

export type ParsedHandling = DeepPartialNotListedAndOverride<HandlingInput, 'loadingUnitIdentifier' | 'type' | 'equipmentType'
    | 'loadingUnitType' | 'loadingUnitSize' | 'originLocode' | 'destinationLocode' | 'shippingAgencyOrCarrier', {
        dangerousGoods: ParsedDangerousGoods[]
    }>

export type ParsedDangerousGoods = DeepPartialNotListed<DangerousGoodsInput, 'id' | 'type' | 'unNumber' | 'packaging' | 'packingGroup'
    | 'provision274' | 'adrClass' | 'adrSecondaryClasses' | 'limitedQuantity' | 'marinePollutant' | 'elevatedTemperature'
    | 'numberOfPackages' | 'weight' | 'flashPoint'>

export type ParseFileOutput = {
    handlings: ParsedHandling[]
};

const parseFile: (file: File) => Promise<ParseFileOutput> = async (file) => {

    const xml = await file.text();

    const valid = validate(xml);

    const handlings: ParsedHandling[] = [];

    if (valid !== true) {
        return { handlings };
    }

    const data = parse(xml, {
        ignoreNameSpace: true,
        arrayMode: (tagName) => {
            return tagName === 'packagedDangerousGoods'
        }
    }) as PmisData;


    forEach(data, (pmisData, k) => {
        if (pmisData === undefined) {
            return;
        }

        const pmisDataType = k as keyof PmisData;

        const type: HandlingType = handlingTypesByPmisDataType[pmisDataType] ?? HandlingType.None;

        forEach(groupBy(pmisData?.packagedDangerousGoodsList?.packagedDangerousGoods as PackagedDangerousGoods[], (c) => c.containerReg??c.trailerPlate), (handlingData, unitNumber) => {

            const { isoContainerCode, provenancePort, destinationPort, containerProperties } = first(handlingData) as PackagedDangerousGoods;

            const dangerousGoods:ParsedDangerousGoods[] = handlingData.map(({ unNumber, goodsClass, technicalName, subsidiaryRisks, flashPoint, limitedQuantity, packingGroup, specialPacking, grossWeight, netWeight, um, marinePollutant, specialPackingType, hot, packagesNumber, packageTypeCode, trailerPlate }) => {

                let weight = netWeight ?? grossWeight ?? null

                const isTons = um.toUpperCase() === PmisUMPackagedDangerousGoods.Tons || um.toLowerCase() === 't';

                if (weight !== null && isTons && weight <= 30) {
                    weight *= 1000;
                }

                const sr = subsidiaryRisks?.toString() ?? '';

                const adrSecondaryClasses = /^[a-z]/i.test(sr) ? [] : map(Array.from(sr.matchAll(subsidiaryRiskRegex)).map(first)) as string[];

                return {
                    __typeName: 'DangerousGoods',
                    id: 0,
                    type,
                    unNumber: padStart((unNumber ?? 0).toString(), 4, '0'),
                    packaging: specialPacking === true ? (specialPackingType?.toString() ?? '') : (packageTypeCode?.toString() ?? ''),
                    packingGroup: pgFromXml(packingGroup ?? ''),
                    provision274: (technicalName as string|number)?.toString().substring(0, 250) ?? null,
                    adrClass: (goodsClass as string|number)?.toString() ?? '',
                    adrSecondaryClasses,
                    limitedQuantity: isBoolean(limitedQuantity) ? limitedQuantity : false,
                    marinePollutant: isBoolean(marinePollutant) ? marinePollutant : false,
                    elevatedTemperature: isBoolean(hot) ? hot : false,
                    numberOfPackages: isNumber(packagesNumber) ? packagesNumber : 0,
                    weight: isNumber(weight) ? weight : 0,
                    flashPoint: flashPoint?.toString() ?? null
                } as ParsedDangerousGoods
            });

            const equipmentType = isoContainerCode?.toString() ?? null;
            const [loadingUnitSize, loadingUnitType] = loadintUnitSizeAndType(equipmentType ?? '');

            handlings.push({
                loadingUnitIdentifier:unitNumber,
                type,
                equipmentType,
                loadingUnitType,
                loadingUnitSize,
                originLocode: provenancePort?.toString() ?? null,
                destinationLocode: destinationPort?.toString() ?? null,
                shippingAgencyOrCarrier: containerProperties?.toString() ?? null,
                dangerousGoods
            });
        });
    });

    return { handlings };
};

export default parseFile;